/**
 * Centralized logging utility with Sentry integration for frontend.
 */

// Environment variables
const CONSOLE_LOGGING = import.meta.env.VITE_CONSOLE_LOGGING !== 'false';
const SENTRY_LOGGING = import.meta.env.VITE_SENTRY_LOGGING === 'true';

/**
 * Send event to Sentry with proper tagging.
 */
function sendToSentry(level, component, message, extra = null, error = null) {
  if (!SENTRY_LOGGING) return;

  try {
    // Import Sentry dynamically (it's initialized in main.jsx)
    const Sentry = window.Sentry;
    if (!Sentry) return;

    // Format message with [frontend] prefix for Sentry visibility
    const sentryMessage = `[frontend][${component}] ${message}`;

    // Set component tag
    Sentry.setTag('component', component);

    // Add extra context if provided
    if (extra) {
      Sentry.setContext('extra', extra);
    }

    // Send to Sentry based on level
    if (error) {
      Sentry.captureException(error, {
        tags: { component },
        contexts: { extra }
      });
    } else if (level === 'critical' || level === 'error') {
      Sentry.captureMessage(sentryMessage, level);
    } else if (level === 'warning') {
      Sentry.captureMessage(sentryMessage, 'warning');
    } else {
      // info level - send as event to make visible in Sentry
      Sentry.captureMessage(sentryMessage, 'info');
      // Also add as breadcrumb for context
      Sentry.addBreadcrumb({
        category: component,
        message: message,
        level: 'info'
      });
    }
  } catch (e) {
    // Never crash on logging failures
    console.error('[logger] Failed to send to Sentry:', e);
  }
}

/**
 * Logger object with methods for different log levels.
 */
const logger = {
  /**
   * Log info-level message.
   */
  info(component, message, extra = null) {
    const formattedMsg = `[${component}] ${message}`;

    if (CONSOLE_LOGGING) {
      console.log(formattedMsg);
    }

    sendToSentry('info', component, message, extra);
  },

  /**
   * Log warning-level message.
   */
  warning(component, message, extra = null) {
    const formattedMsg = `[${component}] ${message}`;

    if (CONSOLE_LOGGING) {
      console.warn(formattedMsg);
    }

    sendToSentry('warning', component, message, extra);
  },

  /**
   * Log error-level message.
   */
  error(component, message, extra = null) {
    const formattedMsg = `[${component}][error] ${message}`;

    if (CONSOLE_LOGGING) {
      console.error(formattedMsg);
    }

    sendToSentry('error', component, message, extra);
  },

  /**
   * Log critical-level message (triggers high-priority alerts).
   */
  critical(component, message, extra = null) {
    const formattedMsg = `[${component}][critical] ${message}`;

    if (CONSOLE_LOGGING) {
      console.error(formattedMsg);
    }

    sendToSentry('critical', component, message, extra);
  },

  /**
   * Log exception with full stack trace.
   */
  exception(component, message, error, extra = null) {
    const formattedMsg = `[${component}][error] ${message}${error ? ': ' + error.message : ''}`;

    if (CONSOLE_LOGGING) {
      console.error(formattedMsg, error);
    }

    sendToSentry('error', component, message, extra, error);
  }
};

export default logger;
